import React, {useEffect, useState} from "react";
import {Button, Modal, Select, Space, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {Wallet} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {getPaymentLinkLiquidationRequests, refundStalePaymentLinkLiquidationRequest} from "../../redux/slice/paymentLinkSlice";
import {HeroHeader, Card, SearchField, formatNaira, COLORS} from "../../designSystem";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";

// NGN is shown as ₦ (brand rule); any other currency keeps its ISO code.
const money = (value: any, currency = "NGN") =>
  !currency || currency === "NGN" ? formatNaira(value) : `${currency} ${Number(value || 0).toLocaleString()}`;

const statusLabel = (value: any, row?: any) => row?.statusText || ({
  1: "Pending admin approval",
  2: "Completed",
  3: "Approved",
  4: "Rejected",
  5: "Payout Pending",
} as Record<number, string>)[Number(value)] || String(value || "Unknown");

const statusColor = (value: any) => ({
  1: "gold",
  2: "green",
  3: "blue",
  4: "red",
  5: "purple",
} as Record<number, string>)[Number(value)] || "default";

const isOlderThanOneDay = (value: string) => {
  if (!value) return false;
  return Date.now() - new Date(value).getTime() > 24 * 60 * 60 * 1000;
};

function PaymentLinkLiquidations() {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {token, permissions, roles} = useSelector((state: RootState) => state.account);
  const canApprove = hasAdminPermission(permissions, roles, ADMIN_PERMISSIONS.paymentLinkLiquidationApprove);
  const {liquidationRequests, pageInfo, loading} = useSelector((state: RootState) => state.paymentLinks);
  const [searchParam, setSearchParam] = useState("");
  const [status, setStatus] = useState<string | undefined>();
  const [refundingId, setRefundingId] = useState<string | null>(null);

  const load = (pageNumber = 1) => {
    if (token) {
      dispatch(getPaymentLinkLiquidationRequests({token, searchParam, status, pageNumber}));
    }
  };

  useEffect(() => {
    load(1);
  }, [token, searchParam, status]);

  const canRefund = (row: any) =>
    Number(row?.status) === 1 &&
    isOlderThanOneDay(row?.dateCreated);

  const refundLiquidation = (row: any) => {
    if (!token || !row?.id || !canRefund(row)) return;

    Modal.confirm({
      title: "Refund stale liquidation",
      content: "This will release the pending liquidation amount back to the payment link available balance. Continue?",
      okText: "Refund balance",
      onOk: async () => {
        setRefundingId(row.id);
        try {
          await dispatch(refundStalePaymentLinkLiquidationRequest({
            token,
            requestId: row.id,
            reason: "Refunded/released by admin after pending for more than 24 hours.",
          })).unwrap();
          message.success("Pending liquidation refunded/released");
          load(pageInfo?.pageNumber || 1);
        } finally {
          setRefundingId(null);
        }
      },
    });
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={Wallet}
          title="Payment Link Liquidations"
          subtitle="Review and approve payout requests from payment link balances."
        />

        <Card className="mt-8 p-5">
          <Space className="mb-4 flex flex-wrap" size={12}>
            <SearchField
              className="min-w-[280px] max-w-xl"
              placeholder="Search liquidation requests"
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
            <Select
              allowClear
              className="w-64"
              placeholder="Filter by status"
              value={status}
              onChange={setStatus}
              options={[
                {label: "Pending admin approval", value: "Processing"},
                {label: "Completed", value: "Completed"},
                {label: "Approved", value: "Validated"},
                {label: "Rejected", value: "Rejected"},
                {label: "Payout pending", value: "PayoutPending"},
              ]}
            />
          </Space>
          <GenericTable
            loading={loading}
            data={liquidationRequests}
            pageInfo={pageInfo}
            handlePageChange={load}
            columns={[
              {title: "Creator", dataIndex: "creatorEmail", key: "creatorEmail"},
              {title: "Amount", key: "amount", render: (_: any, row: any) => <span className="font-mono">{money(row.amount, row.currency)}</span>},
              {title: "Bank", dataIndex: "bankName", key: "bankName"},
              {title: "Account", key: "account", render: (_: any, row: any) => `${row.accountName || ""} ${row.accountNumber || ""}`},
              {title: "Status", dataIndex: "status", key: "status", render: (value: any, row: any) => <Tag color={statusColor(value)}>{statusLabel(value, row)}</Tag>},
              {title: "Created", dataIndex: "dateCreated", key: "dateCreated", render: (value: string) => value ? new Date(value).toLocaleString() : ""},
              {title: "Action", key: "action", render: (_: any, row: any) => (
                <Space size={8} wrap>
                  <Button onClick={() => navigate(`/payment-link-liquidations/${row.id}`)}>Review</Button>
                  {canApprove && Number(row.status) === 1 && (
                    <Button onClick={() => navigate(`/payment-link-liquidations/${row.id}?method=manual`)}>Manual transfer</Button>
                  )}
                  {canRefund(row) ? (
                    <Button
                      danger
                      loading={refundingId === row.id}
                      disabled={!!refundingId}
                      onClick={() => refundLiquidation(row)}
                    >
                      Refund balance
                    </Button>
                  ) : null}
                </Space>
              )},
            ]}
          />
        </Card>
      </main>
    </AdminLayout>
  );
}

export default PaymentLinkLiquidations;
